<template>
    <main class="statement-page" v-loading="loading">
        <section class="statement-card">
            <header class="statement-header">
                <div class="statement-title">
                    <el-button icon="el-icon-arrow-left" @click="goBack"></el-button>
                    <div>
                        <h1>{{$t('createStatement.pageTitle')}}</h1>
                        <p>{{client.name}}</p>
                    </div>
                </div>
                <div class="statement-actions">
                    <el-button icon="el-icon-edit" @click="editVisible = true">{{$t('common.edit')}}</el-button>
                    <el-button icon="el-icon-printer" @click="printStatement">{{$t('common.print')}}</el-button>
                    <el-button type="primary" icon="el-icon-message" @click="openEmailDialog">{{$t('common.email')}}</el-button>
                </div>
            </header>

            <section class="statement-summary">
                <div><span>{{$t('createStatement.from')}}</span><strong>{{formattedStartDate}}</strong></div>
                <div><span>{{$t('createStatement.to')}}</span><strong>{{formattedEndDate}}</strong></div>
                <div><span>{{$t('createStatement.invoices')}}</span><strong>{{includedInvoices.length}}</strong></div>
                <div><span>{{$t('createStatement.balanceDue')}}</span><strong>{{money(balanceDue)}}</strong></div>
            </section>

            <el-alert v-if="onlyShowUnpaid" :title="$t('createStatement.unpaidOnlyActive')" type="info" :closable="false" show-icon></el-alert>
            <iframe v-if="previewUrl !== ''" :src="previewUrl" class="statement-preview" frameborder="0" @load="previewLoading = false"></iframe>
        </section>

        <el-dialog :title="$t('createStatement.editTitle')" :visible.sync="editVisible" width="560px" :close-on-click-modal="false">
            <el-form label-position="top">
                <div class="statement-date-grid">
                    <el-form-item :label="$t('createStatement.startDate')">
                        <el-date-picker v-model="draftStartDate" type="date" style="width: 100%"></el-date-picker>
                    </el-form-item>
                    <el-form-item :label="$t('createStatement.endDate')">
                        <el-date-picker v-model="draftEndDate" type="date" style="width: 100%"></el-date-picker>
                    </el-form-item>
                </div>
                <el-checkbox v-model="draftOnlyShowUnpaid">{{$t('createStatement.onlyShowUnpaid')}}</el-checkbox>
            </el-form>
            <div slot="footer">
                <el-button @click="editVisible = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" @click="applyStatementOptions">{{$t('common.save')}}</el-button>
            </div>
        </el-dialog>

        <el-dialog :title="$t('common.sendEmail')" :visible.sync="emailVisible" width="640px"
                   custom-class="document-email-dialog" :close-on-click-modal="false">
            <div class="document-email-form">
                <div class="document-email-field">
                    <label>{{$t('invoiceCreate.labelTo')}}</label>
                    <el-input v-model="emailForm.toEmail"></el-input>
                </div>
                <div class="document-email-row">
                    <div class="document-email-field"><label>{{$t('invoiceCreate.cc')}}</label><el-input v-model="emailForm.ccEmail"></el-input></div>
                    <div class="document-email-field"><label>{{$t('invoiceCreate.bcc')}}</label><el-input v-model="emailForm.bccEmail"></el-input></div>
                </div>
                <div class="document-email-field"><label>{{$t('common.subject')}}</label><el-input v-model="emailForm.subject"></el-input></div>
                <div class="document-email-field"><label>{{$t('default.emailMessage')}}</label><el-input v-model="emailForm.content" type="textarea" :rows="6"></el-input></div>
                <div class="document-email-attachment">
                    <div class="document-email-pdf-icon">PDF</div>
                    <div><strong>{{attachmentName}}</strong><span>{{$t('invoiceCreate.pdf')}}</span></div>
                </div>
            </div>
            <div slot="footer" class="document-email-footer">
                <el-button :disabled="emailSending" @click="emailVisible = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :loading="emailSending" @click="sendEmail">{{$t('common.send')}}</el-button>
            </div>
        </el-dialog>
    </main>
</template>

<script>
    import moment from 'moment-timezone'
    import {timest, validateEmail} from '@/utils/validate'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'

    export default {
        data() {
            const endDate = moment().startOf('day')
            return {
                loading: true,
                previewLoading: true,
                client: {innerUUID: '', name: '', clientEmail: ''},
                invoices: [],
                startDate: endDate.clone().subtract(3, 'months').toDate(),
                endDate: endDate.toDate(),
                onlyShowUnpaid: false,
                draftStartDate: endDate.clone().subtract(3, 'months').toDate(),
                draftEndDate: endDate.toDate(),
                draftOnlyShowUnpaid: false,
                editVisible: false,
                emailVisible: false,
                emailSending: false,
                emailForm: {toEmail: '', ccEmail: '', bccEmail: '', subject: '', content: ''},
                userSetting: JSON.parse(localStorage.getItem('userSetting')),
                userCompany: JSON.parse(localStorage.getItem('userCompany'))
            }
        },
        computed: {
            formattedStartDate() { return moment(this.startDate).format('MMM D, YYYY') },
            formattedEndDate() { return moment(this.endDate).format('MMM D, YYYY') },
            startTimestamp() { return moment(this.startDate).startOf('day').unix() },
            endExclusiveTimestamp() { return moment(this.endDate).startOf('day').add(1, 'day').unix() },
            includedInvoices() {
                return this.invoices.filter(invoice => {
                    const start = Number(invoice.invoiceStartDate)
                    return invoice.syncStatus === 0
                        && start >= this.startTimestamp
                        && start < this.endExclusiveTimestamp
                        && (!this.onlyShowUnpaid || invoice.paidStatus !== 2)
                })
            },
            balanceDue() {
                return this.includedInvoices.reduce((sum, invoice) => this.$NP.plus(sum, Number(invoice.balanceDueMoney)), 0)
            },
            previewUrl() {
                if (this.client.innerUUID === '') return ''
                const loginInformation = JSON.parse(localStorage.getItem('loginInformation')).objectData
                const url = new URL('preview_pdf', this.$store.state.requestURL)
                url.searchParams.set('clientUUID', this.client.innerUUID)
                url.searchParams.set('companyUUID', this.userCompany.innerUUID)
                url.searchParams.set('email', loginInformation.email)
                url.searchParams.set('nowTime', timest())
                url.searchParams.set('createType', '10')
                url.searchParams.set('timeont', moment.tz.guess())
                url.searchParams.set('startInvoiceDate', String(this.startTimestamp))
                url.searchParams.set('endInvoiceDate', String(this.endExclusiveTimestamp))
                url.searchParams.set('onlyShowUnpaidInvoice', this.onlyShowUnpaid ? '1' : '0')
                url.searchParams.set('requestId', String(Date.now()))
                return url.toString()
            },
            attachmentName() {
                const safeName = this.userCompany.companyName.replace(/[^A-Za-z0-9_-]/g, '-')
                return `Statement_from_${safeName}.pdf`
            }
        },
        methods: {
            async load() {
                const clientUUID = this.$route.query.clientUUID
                if (typeof clientUUID !== 'string' || clientUUID === '') {
                    throw new Error('Client UUID is required')
                }
                const clientResponse = await this.$axios.post('query_user_client', {params: {innerUUID: clientUUID}})
                if (clientResponse.data.errorCode !== 0 || clientResponse.data.objectData == null) {
                    throw new Error('Client response is invalid')
                }
                this.client = clientResponse.data.objectData
                await this.loadAllInvoices()
            },
            async loadAllInvoices() {
                const pageSize = 400
                let currentPage = 1
                let totalCount = 0
                const invoices = []
                do {
                    const response = await this.$axios.post('query_invoice_list_user_web', {params: {
                        currentPage, pageSize, numberOrClient: '', orderName: 'invoiceStartDate', orderTpe: 2,
                        type: 0, clientUUID: this.client.innerUUID
                    }})
                    if (response.data.errorCode !== 0 || !Array.isArray(response.data.dataList)
                        || !Number.isInteger(response.data.totalCount) || response.data.totalCount < 0) {
                        throw new Error('Invoice list response is invalid')
                    }
                    totalCount = response.data.totalCount
                    if (response.data.dataList.length === 0 && invoices.length < totalCount) {
                        throw new Error('Invoice list pagination response is invalid')
                    }
                    invoices.push(...response.data.dataList)
                    currentPage += 1
                } while (invoices.length < totalCount)
                this.invoices = invoices
            },
            applyStatementOptions() {
                if (this.draftStartDate == null || this.draftEndDate == null) {
                    this.$message.error(this.$t('createStatement.dateRequired'))
                    return
                }
                if (moment(this.draftEndDate).isBefore(moment(this.draftStartDate), 'day')) {
                    this.$message.error(this.$t('createStatement.invalidDateRange'))
                    return
                }
                this.startDate = this.draftStartDate
                this.endDate = this.draftEndDate
                this.onlyShowUnpaid = this.draftOnlyShowUnpaid
                this.previewLoading = true
                this.editVisible = false
            },
            async fetchPdf() {
                const response = await fetch(this.previewUrl, {method: 'GET', cache: 'no-store', credentials: 'omit'})
                if (!response.ok) throw new Error(`${this.$t('common.pdfPreparationFailed')} (HTTP ${response.status})`)
                const pdf = await response.blob()
                if (await pdf.slice(0, 5).text() !== '%PDF-') throw new Error(this.$t('common.pdfPreparationFailed'))
                return pdf
            },
            async printStatement() {
                const loading = this.$loading({lock: true, text: this.$t('common.preparingPdf')})
                try {
                    const pdf = await this.fetchPdf()
                    const url = URL.createObjectURL(pdf)
                    const frame = document.createElement('iframe')
                    frame.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;border:0'
                    frame.onload = () => { frame.contentWindow.focus(); frame.contentWindow.print(); window.setTimeout(() => { URL.revokeObjectURL(url); frame.remove() }, 60000) }
                    frame.src = url
                    document.body.appendChild(frame)
                } catch (error) {
                    this.$message.error(error.message)
                } finally {
                    loading.close()
                }
            },
            async openEmailDialog() {
                const loading = this.$loading({lock: true, text: this.$t('common.preparingPdf')})
                try {
                    await this.fetchPdf()
                    this.emailForm = {
                        toEmail: this.client.clientEmail,
                        ccEmail: this.userSetting.emailCc,
                        bccEmail: this.userSetting.emailBcc,
                        subject: `Statement from ${this.userCompany.companyName}`,
                        content: this.userSetting.emailMessage
                    }
                    this.emailVisible = true
                } catch (error) {
                    this.$message.error(error.message)
                } finally {
                    loading.close()
                }
            },
            async sendEmail() {
                if (!validateEmail(this.emailForm.toEmail)) {
                    this.$message.error(this.$t('common.validEmail'))
                    return
                }
                this.emailSending = true
                try {
                    const response = await this.$axios.post('send_email_to_client', {params: {
                        clientUUID: this.client.innerUUID,
                        companyUUID: this.userCompany.innerUUID,
                        toEmail: this.emailForm.toEmail,
                        subject: this.emailForm.subject,
                        copyEmail: this.emailForm.ccEmail,
                        secretEmail: this.emailForm.bccEmail,
                        content: this.emailForm.content,
                        pdfName: this.attachmentName,
                        timeont: moment.tz.guess(),
                        nowTime: timest(),
                        createType: 10,
                        startInvoiceDate: this.startTimestamp,
                        endInvoiceDate: this.endExclusiveTimestamp,
                        onlyShowUnpaidInvoice: this.onlyShowUnpaid ? 1 : 0
                    }})
                    if (response.data.errorCode === 0) {
                        this.emailVisible = false
                        this.$message.success(this.$t('common.sendSuccess'))
                    }
                } finally {
                    this.emailSending = false
                }
            },
            goBack() { this.$router.push({path: '/clientUpdate', query: {innerUUID: this.client.innerUUID}}) },
            money(value) { return xiaoshudianfenge(value, this.userSetting) }
        },
        mounted() {
            this.load().catch(error => this.$message.error(error.message)).finally(() => { this.loading = false })
        }
    }
</script>

<style scoped>
    .statement-page { min-height: 100%; padding: 30px; box-sizing: border-box; background: #f4f7fb; }
    .statement-card { max-width: 1260px; margin: 0 auto; padding: 0 28px 28px; background: #fff; border: 1px solid #e4eaf3; border-radius: 16px; box-shadow: 0 12px 34px rgba(41,62,96,.08); }
    .statement-header { display: flex; justify-content: space-between; align-items: center; min-height: 94px; border-bottom: 1px solid #e8edf5; }
    .statement-title { display: flex; align-items: center; gap: 16px; }
    .statement-title h1 { margin: 0; color: #17213c; font-size: 24px; }
    .statement-title p { margin: 5px 0 0; color: #8492a8; }
    .statement-actions { display: flex; gap: 10px; }
    .statement-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding: 24px 0; }
    .statement-summary div { padding: 18px; background: #f7f9fd; border: 1px solid #e5ebf3; border-radius: 11px; }
    .statement-summary span, .statement-summary strong { display: block; }
    .statement-summary span { color: #8290a5; font-size: 13px; }
    .statement-summary strong { margin-top: 7px; color: #263653; font-size: 19px; }
    .statement-preview { width: 100%; height: 760px; margin-top: 18px; border: 1px solid #e4eaf3; border-radius: 10px; }
    .statement-date-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
    @media (max-width: 800px) { .statement-page { padding: 14px; } .statement-header { align-items: flex-start; flex-direction: column; padding: 20px 0; gap: 16px; } .statement-summary { grid-template-columns: 1fr 1fr; } }
</style>
